import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent } from '../ui/card'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Badge } from '../ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog'
import { Switch } from '../ui/switch'
import {
  Palette,
  Plus,
  RefreshCw,
  Search,
  Loader2,
  MoreHorizontal,
  Edit,
  Trash2,
  Copy,
  CreditCard,
} from 'lucide-react'
import BadgeTemplateEndpoints, {
  BadgeTemplate,
} from '../../endpoints/BadgeTemplateEndpoints'
import { useSessionStore } from '../../stores/useSessionStore'
import { useApplicationStore } from '../../stores/useApplicationStore'
import { Globe, Users } from 'lucide-react'
import { toast } from 'sonner'
import { extractErrorMessage } from '@/common/extractErrorMessage'

export const BadgeTemplatesPage: React.FC = () => {
  const navigate = useNavigate()
  const { hasAdmin } = useSessionStore()
  const { clients, getClientById } = useApplicationStore()
  const [templates, setTemplates] = useState<BadgeTemplate[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [templateToDelete, setTemplateTo] = useState<BadgeTemplate | null>(null)
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false)
  const [templateToDuplicate, setTemplateToDuplicate] =
    useState<BadgeTemplate | null>(null)
  const [duplicateName, setDuplicateName] = useState('')

  const loadTemplates = async () => {
    setLoading(true)
    try {
      const data = await BadgeTemplateEndpoints.getTemplates()
      setTemplates(data)
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to load templates'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadTemplates()
  }, [])

  const filtered = templates.filter(
    (t) =>
      !searchTerm || t.name.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const handleToggleActive = async (template: BadgeTemplate) => {
    try {
      await BadgeTemplateEndpoints.toggleActive(template.id, !template.isActive)
      await loadTemplates()
      toast.success(
        `Template ${template.isActive ? 'deactivated' : 'activated'}`
      )
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to update template'))
    }
  }

  const handleDelete = async () => {
    if (!templateToDelete) return
    try {
      await BadgeTemplateEndpoints.deleteTemplate(templateToDelete.id)
      await loadTemplates()
      toast.success('Template deleted')
      setDeleteDialogOpen(false)
    } catch (err: any) {
      toast.error(extractErrorMessage(err, 'Failed to delete template'))
    }
  }

  const handleDuplicate = async () => {
    if (!templateToDuplicate || !duplicateName.trim()) return
    try {
      const newTemplate = await BadgeTemplateEndpoints.duplicateTemplate(
        templateToDuplicate.id,
        duplicateName.trim()
      )
      await loadTemplates()
      toast.success('Template duplicated')
      setDuplicateDialogOpen(false)
      navigate(`/admin/badge-templates/${newTemplate.id}/edit`)
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to duplicate template'))
    }
  }

  const categoryColor = (cat: string) => {
    switch (cat) {
      case 'DEFAULT':
        return 'bg-blue-50 text-blue-700'
      case 'MODERN':
        return 'bg-violet-50 text-violet-700'
      case 'CUSTOM':
        return 'bg-amber-50 text-amber-700'
      case 'PREMIUM':
        return 'bg-emerald-50 text-emerald-700'
      default:
        return ''
    }
  }

  return (
    <div className="container mx-auto px-4 py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
            <Palette className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">
              Badge Templates
            </h1>
            <p className="text-sm text-muted-foreground">
              {templates.length} template{templates.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={loadTemplates}>
            <RefreshCw className="h-4 w-4" />
          </Button>
          {hasAdmin() && (
            <Button onClick={() => navigate('/admin/badge-templates/new')}>
              <Plus className="mr-2 h-4 w-4" />
              New Template
            </Button>
          )}
        </div>
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search templates..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              <p className="mt-3 text-sm text-muted-foreground">
                Loading templates...
              </p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16">
              <CreditCard className="h-10 w-10 text-muted-foreground mb-3" />
              <p className="text-sm font-medium">No templates found</p>
              <p className="text-sm text-muted-foreground mt-1">
                Create your first badge template
              </p>
              {hasAdmin() && (
                <Button
                  className="mt-4"
                  onClick={() => navigate('/admin/badge-templates/new')}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  New Template
                </Button>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Name</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Orientation</TableHead>
                  <TableHead className="text-center">Type</TableHead>
                  <TableHead>Visibility</TableHead>
                  <TableHead className="text-center">Active</TableHead>
                  <TableHead className="w-[50px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((template) => (
                  <TableRow key={template.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{template.name}</p>
                        {template.description && (
                          <p className="text-xs text-muted-foreground">
                            {template.description}
                          </p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="secondary"
                        className={`text-xs ${categoryColor(template.category)}`}
                      >
                        {template.category}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {template.orientation}
                    </TableCell>
                    <TableCell className="text-center">
                      {template.isSystem ? (
                        <Badge variant="outline" className="text-xs">
                          System
                        </Badge>
                      ) : template.isLayoutBased ? (
                        <Badge
                          variant="secondary"
                          className="text-xs bg-green-50 text-green-700"
                        >
                          Visual
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="text-xs">
                          SVG
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {template.isGlobal ? (
                        <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                          <Globe className="h-3.5 w-3.5" />
                          <span>Global</span>
                        </div>
                      ) : template.clientIds.length > 0 ? (
                        <div className="flex items-center gap-1.5">
                          <Users className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                          <div className="flex flex-wrap gap-1">
                            {template.clientIds.map((cid) => {
                              const client = getClientById(cid)
                              return (
                                <Badge
                                  key={cid}
                                  variant="outline"
                                  className="text-[10px] px-1.5 py-0"
                                >
                                  {client?.name || `#${cid}`}
                                </Badge>
                              )
                            })}
                          </div>
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground italic">
                          No clients
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <Switch
                        checked={template.isActive}
                        onCheckedChange={() => handleToggleActive(template)}
                        disabled={template.isSystem}
                      />
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {!template.isSystem && (
                            <DropdownMenuItem
                              onClick={() =>
                                navigate(
                                  `/admin/badge-templates/${template.id}/edit`
                                )
                              }
                            >
                              <Edit className="h-4 w-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem
                            onClick={() => {
                              setTemplateToDuplicate(template)
                              setDuplicateName(`${template.name} Copy`)
                              setDuplicateDialogOpen(true)
                            }}
                          >
                            <Copy className="h-4 w-4 mr-2" />
                            Duplicate
                          </DropdownMenuItem>
                          {!template.isSystem && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                className="text-destructive focus:text-destructive"
                                onClick={() => {
                                  setTemplateTo(template)
                                  setDeleteDialogOpen(true)
                                }}
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Template</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{templateToDelete?.name}"? This
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={duplicateDialogOpen} onOpenChange={setDuplicateDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Duplicate Template</DialogTitle>
            <DialogDescription>Enter a name for the copy.</DialogDescription>
          </DialogHeader>
          <Input
            value={duplicateName}
            onChange={(e) => setDuplicateName(e.target.value)}
            placeholder="New template name"
            autoFocus
          />
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDuplicateDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button onClick={handleDuplicate} disabled={!duplicateName.trim()}>
              Duplicate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
